/**
 * Cart Hooks
 */

'use client';

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GetCartUseCase } from '@/application/use-cases/get-cart.use-case';
import { UpdateCartItemUseCase } from '@/application/use-cases/update-cart-item.use-case';
import { RemoveCartItemUseCase } from '@/application/use-cases/remove-cart-item.use-case';
import { CreateCartUseCase } from '@/application/use-cases/create-cart.use-case';
import { cartService } from '@/infrastructure/config/services.config';
import { storefrontLayoutLogger } from '@/lib/logger';

const CART_TOKEN_STORAGE_KEY = 'cart_token';

const getCartUseCase = new GetCartUseCase(cartService);
const updateCartItemUseCase = new UpdateCartItemUseCase(cartService);
const removeCartItemUseCase = new RemoveCartItemUseCase(cartService);
const createCartUseCase = new CreateCartUseCase(cartService);

export function useCart(cartToken: string | null, onCartCompleted?: () => void) {
  const queryClient = useQueryClient();
  
  const query = useQuery({
    queryKey: ['cart', cartToken],
    queryFn: async () => {
      if (!cartToken) {
        return null;
      }
      const result = await getCartUseCase.execute({ token: cartToken });
      return result.cart;
    },
    enabled: !!cartToken,
    staleTime: 30 * 1000, // 30 seconds
    retry: 1,
  });

  // Handle completed cart status
  // If cart is completed (converted to order), clear the token so a new cart will be created
  useEffect(() => {
    if (query.data?.status === 'completed') {
      storefrontLayoutLogger.info('[useCart] Cart is completed, clearing token to create new cart');

      // Clear from localStorage
      if (typeof window !== 'undefined') {
        localStorage.removeItem(CART_TOKEN_STORAGE_KEY);
      }

      // Clear from React Query cache
      queryClient.removeQueries({ queryKey: ['cart', cartToken] });

      // Notify parent component (typically cart context) to clear its state
      if (onCartCompleted) {
        onCartCompleted();
      }
    }
  }, [query.data?.status, cartToken, queryClient, onCartCompleted]);

  // Handle stale cart token — if we have a token but getCart returned null (cart not found),
  // clear the stale token from localStorage so future add-to-cart creates a fresh cart
  useEffect(() => {
    if (cartToken && !query.isLoading && !query.isFetching && query.data === null) {
      storefrontLayoutLogger.info('[useCart] Stale cart token detected (cart not found), clearing localStorage');

      if (typeof window !== 'undefined') {
        localStorage.removeItem(CART_TOKEN_STORAGE_KEY);
      }

      queryClient.removeQueries({ queryKey: ['cart', cartToken] });

      if (onCartCompleted) {
        onCartCompleted();
      }
    }
  }, [cartToken, query.isLoading, query.isFetching, query.data, queryClient, onCartCompleted]);

  return query;
}

export function useUpdateCartItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      cartToken: string;
      productId: string;
      variantId: string;
      quantity: number;
    }) => {
      return updateCartItemUseCase.execute(input);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cart', variables.cartToken] });
    },
  });
}

export function useRemoveCartItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      cartToken: string;
      productId: string;
      variantId: string;
    }) => {
      return removeCartItemUseCase.execute(input);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cart', variables.cartToken] });
    },
  });
}

export function useUpdatePackQuantity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { cartToken: string; packId: string; quantity: number }) => {
      const cart = await cartService.updatePackQuantity(
        input.cartToken,
        input.packId,
        input.quantity,
      );
      return { cart };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cart', variables.cartToken] });
    },
  });
}

export function useRemovePackFromCart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { cartToken: string; packId: string }) => {
      const cart = await cartService.removePackFromCart(input.cartToken, input.packId);
      return { cart };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cart', variables.cartToken] });
    },
  });
}

export function useCreateCart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input?: {
      countryCode?: string;
      currency?: string;
      vendor?: string;
      shop?: string;
    }) => {
      return createCartUseCase.execute({ options: input });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['cart', data.cart.token], data.cart);
    },
  });
}
